import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  PlayArrow as RunIcon,
} from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import PropTypes from "prop-types";

import { Can } from "src/helpers/Auth";
import ErrorMessageAlerts from "src/reusable/ErrorMessageAlerts";
import { ModelVersionDatasetSelect } from "src/views/forms/DatasetModals";
import { useGetDataset } from "src/views/datasets/services/DatasetServices";

import {
  useAutoflowDatasetRunnability,
  useAutoflowRunStatus,
  useAutomationConfigTemplate,
  useCreateAutoFlowRun,
} from "../services/AutomationListServices";
import {
  useAutomationEfficiency,
  useAutomationAccuracy,
  useAutomationCount,
} from "../services/AutomationRecordServices";
import { AutomationConfigTemplateTable } from "../AutomationConfigTemplateList";
import { AutoFlowLoadingBar } from "./components/AutoFlowLoadingBar";
import { TemplatePageHeader, TemplateTabs } from "../shared/TemplateNav";
import ScreeningKindBadge from "../llm/ScreeningKindBadge";
import { isLlmTemplate } from "../llm/llmUtils";
import { useLlmScreeningEnabled } from "src/helpers/licensedRolloutGates";

const formatPct = (v) => {
  if (v == null || v < 0) return "—";
  return `${(v * 100).toFixed(2)}%`;
};

const KpiCard = ({ label, value, isLoading, testId, accent }) => (
  <Card variant="outlined" sx={{ height: "100%" }} data-testid={testId}>
    <CardContent>
      <Typography variant="overline" color="text.secondary" gutterBottom>
        {label}
      </Typography>
      <Typography
        variant="h4"
        sx={{ fontWeight: 600, color: accent || "primary.main", mt: 0.5 }}
      >
        {isLoading ? "…" : value}
      </Typography>
    </CardContent>
  </Card>
);

KpiCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
  isLoading: PropTypes.bool,
  testId: PropTypes.string,
  accent: PropTypes.string,
};

const EfficiencyCard = ({ templateId }) => {
  const { data, isLoading } = useAutomationEfficiency(templateId);
  return (
    <KpiCard
      label="Automation Efficiency"
      value={formatPct(data)}
      isLoading={isLoading}
      testId="cap-tmpl-overview-stat-efficiency"
    />
  );
};
EfficiencyCard.propTypes = {
  templateId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
};

const AccuracyCard = ({ templateId }) => {
  const { data, isLoading } = useAutomationAccuracy(templateId);
  return (
    <KpiCard
      label="Critical Accuracy"
      value={formatPct(data)}
      isLoading={isLoading}
      testId="cap-tmpl-overview-stat-accuracy"
      accent="success.main"
    />
  );
};
AccuracyCard.propTypes = {
  templateId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
};

const CountCard = ({ templateId }) => {
  const { data, isLoading } = useAutomationCount(templateId);
  const formatted = data == null ? "—" : Number(data).toLocaleString();
  return (
    <KpiCard
      label="Records Automated"
      value={formatted}
      isLoading={isLoading}
      testId="cap-tmpl-overview-stat-automated"
      accent="info.main"
    />
  );
};
CountCard.propTypes = {
  templateId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
};

/**
 * Dataset picker for a manual AutoFlow run. Every candidate is listed; the
 * ones this template cannot run on are disabled and carry the reason, because
 * an empty dropdown told the user nothing about what to fix.
 *
 * The runnability endpoint ships in model-runtime-engine#30883. Until it is
 * deployed it 404s, so the picker falls back to the model-version one it
 * replaces rather than leaving Run disabled for every template.
 */
export const AutoflowDatasetSelect = ({
  templateId,
  template,
  selectedDatasetId,
  onSelect,
}) => {
  const { data, isLoading, isError, error } =
    useAutoflowDatasetRunnability(templateId);
  const rows = data || [];
  const blockedCount = rows.filter((r) => !r.runnable).length;

  if (error?.response?.status === 404) {
    return (
      <Box data-testid="cap-tmpl-dataset-select-fallback">
        <ModelVersionDatasetSelect
          modelId={template?.current_config_model_id}
          modelVersionNum={template?.current_config_model_version_number}
          handleSelectedDataset={onSelect}
        />
      </Box>
    );
  }
  if (isError) {
    return (
      <Alert severity="warning" data-testid="cap-tmpl-dataset-select-error">
        Could not load which datasets this template can run on.
      </Alert>
    );
  }
  if (!isLoading && rows.length === 0) {
    return (
      <Alert severity="info" data-testid="cap-tmpl-dataset-select-empty">
        No tabular datasets exist yet — upload one to run AutoFlow.
      </Alert>
    );
  }

  return (
    <>
      <TextField
        select
        fullWidth
        size="small"
        label={isLoading ? "Loading datasets…" : "Dataset"}
        disabled={isLoading}
        value={selectedDatasetId ?? ""}
        onChange={(event) => onSelect(event.target.value)}
        data-testid="cap-tmpl-dataset-select"
        SelectProps={{
          MenuProps: { PaperProps: { sx: { maxHeight: "40vh" } } },
        }}
      >
        {rows.map((row) => (
          <MenuItem
            key={row.dataset_id}
            value={row.dataset_id}
            disabled={!row.runnable}
            data-testid={`cap-tmpl-dataset-option-${row.dataset_id}`}
          >
            <Box>
              <Typography variant="body2">
                {row.name} ({row.record_count} records)
              </Typography>
              {row.issues?.length > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {row.issues.join(" · ")}
                </Typography>
              )}
            </Box>
          </MenuItem>
        ))}
      </TextField>
      {blockedCount > 0 && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 0.5 }}
          data-testid="cap-tmpl-dataset-blocked-count"
        >
          {blockedCount} dataset(s) cannot be run on by this template.
        </Typography>
      )}
    </>
  );
};

AutoflowDatasetSelect.propTypes = {
  templateId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  /** Only read on the pre-#30883 fallback path. */
  template: PropTypes.object,
  selectedDatasetId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onSelect: PropTypes.func.isRequired,
};

const ManualAutoFlowCard = ({ template, templateId }) => {
  const [errorMessages, setErrorMessages] = useState([]);
  const [datasetFk, setDatasetFk] = useState(null);
  const [running, setRunning] = useState(false);
  const llmScreeningEnabled = useLlmScreeningEnabled();
  const { data: runStatus, isSuccess: runStatusLoaded } = useAutoflowRunStatus(
    templateId,
    datasetFk,
  );
  const { mutate } = useCreateAutoFlowRun();

  const handleRun = () => {
    setErrorMessages([]);
    setRunning(true);
    mutate(
      {
        automationConfigTemplateId: templateId,
        datasetId: datasetFk,
      },
      {
        onError: (e) => setErrorMessages([e.message]),
        onSettled: () => setRunning(false),
      },
    );
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 600 }}
            data-testid="cap-tmpl-heading-manual-ops"
          >
            Run AutoFlow manually
          </Typography>
          <Chip label="Advanced" size="small" variant="outlined" />
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Run a dataset through this template&apos;s AutoFlow pipeline without
          waiting for the scheduled run. Datasets this template cannot run on
          are listed too, with the reason.
        </Typography>
        {llmScreeningEnabled && isLlmTemplate(template) && (
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            data-testid="cap-tmpl-alert-manual-run-llm"
          >
            This template screens with an LLM, so it has no model version to map
            datasets to and none are listed here yet.
          </Alert>
        )}

        <Box data-testid="cap-tmpl-overview-dataset-select" sx={{ mb: 2 }}>
          <AutoflowDatasetSelect
            templateId={templateId}
            template={template}
            selectedDatasetId={datasetFk}
            onSelect={(x) => setDatasetFk(x)}
          />
        </Box>

        <Stack direction="row" spacing={2} alignItems="center">
          <Button
            variant="contained"
            color="success"
            startIcon={<RunIcon />}
            disabled={running || !datasetFk}
            onClick={handleRun}
            data-testid="cap-tmpl-btn-run-autoflow"
            data-attr="cap-ai:btn:run-autoflow"
            sx={{ textTransform: "none" }}
          >
            {running ? "Starting…" : "Run AutoFlow"}
          </Button>
          <Box sx={{ flex: 1 }}>
            {errorMessages.length > 0 && (
              <ErrorMessageAlerts
                errorMessages={errorMessages}
                style={{ marginBottom: 0 }}
              />
            )}
            {runStatusLoaded && runStatus.pct_complete === -1 && (
              <Alert
                severity="error"
                sx={{ m: 0 }}
                data-testid="cap-tmpl-alert-run-error"
              >
                {runStatus.message}
                <br />
                Run started by: {runStatus.username}
              </Alert>
            )}
            {runStatusLoaded && runStatus.pct_complete !== -1 && (
              <AutoFlowLoadingBar
                ranByUser={runStatus.username}
                value={runStatus.pct_complete * 100}
              />
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

ManualAutoFlowCard.propTypes = {
  template: PropTypes.object,
  templateId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
};

const AdvancedDetailsCard = ({ template, templateId, groundTruthDataset }) => {
  const [open, setOpen] = useState(false);
  const llmScreeningEnabled = useLlmScreeningEnabled();
  return (
    <Card variant="outlined">
      <CardContent sx={{ pb: open ? 2 : "16px !important" }}>
        <Box
          onClick={() => setOpen((v) => !v)}
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            userSelect: "none",
          }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setOpen((v) => !v);
            }
          }}
          data-testid="cap-tmpl-advanced-toggle"
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, flex: 1 }}
            data-testid="cap-tmpl-heading-details"
          >
            Advanced details
          </Typography>
          {open ? <CollapseIcon /> : <ExpandIcon />}
        </Box>

        <Collapse in={open} timeout="auto" unmountOnExit>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Typography variant="overline" color="text.secondary">
                  Ground Truth Dataset
                </Typography>
                <Typography
                  variant="body2"
                  data-testid="cap-tmpl-detail-gt-dataset"
                >
                  {groundTruthDataset?.name ?? "—"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={8}>
                <Typography variant="overline" color="text.secondary">
                  Models
                </Typography>
                <ScreeningKindBadge
                  screeningKind={template?.screening_kind}
                  llmScreeningEnabled={llmScreeningEnabled}
                  testId="cap-tmpl-detail-screening-kind"
                  sx={{ ml: 1 }}
                />
                <Box data-testid="cap-tmpl-detail-models">
                  {llmScreeningEnabled && isLlmTemplate(template) ? (
                    <Typography variant="body2" color="text.secondary">
                      None: each config&apos;s screening persona supplies the
                      model.
                    </Typography>
                  ) : template?.automation_config_template_models?.length ? (
                    <Stack
                      direction="row"
                      spacing={1}
                      flexWrap="wrap"
                      useFlexGap
                    >
                      {template.automation_config_template_models.map(
                        (model) => (
                          <Chip
                            key={model.id}
                            component={RouterLink}
                            to={`/modeldetails/${model.model_fk}`}
                            clickable
                            size="small"
                            variant="outlined"
                            label={model.model_name}
                            data-testid={`cap-tmpl-link-model-${model.model_fk}`}
                            data-attr={`cap-ai:link:view-model-${model.model_fk}`}
                          />
                        ),
                      )}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      —
                    </Typography>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  data-testid="cap-tmpl-heading-children"
                >
                  Child templates
                </Typography>
                <Box data-testid="cap-tmpl-table-children" sx={{ mt: 0.5 }}>
                  <AutomationConfigTemplateTable
                    parentAutomationConfigTemplateId={templateId}
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="overline" color="text.secondary">
                  Raw Template JSON
                </Typography>
                <Box
                  component="pre"
                  data-testid="cap-tmpl-detail-raw-data"
                  data-attr="cap-ai:input:automation-template-raw-data"
                  sx={{
                    m: 0,
                    p: 2,
                    bgcolor: "action.hover",
                    borderRadius: 1,
                    fontSize: "0.75rem",
                    fontFamily: "monospace",
                    maxHeight: 360,
                    overflow: "auto",
                  }}
                >
                  {JSON.stringify(template?.rawdata, null, 2)}
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

AdvancedDetailsCard.propTypes = {
  template: PropTypes.object,
  templateId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  groundTruthDataset: PropTypes.object,
};

export const AutomationConfigTemplateDetail = (props) => {
  const templateId = props.match.params.automation_config_template_id;

  const { data: template } = useAutomationConfigTemplate(templateId);
  const { data: groundTruthDataset } = useGetDataset(
    template?.ground_truth_dataset_fk,
  );

  return (
    <Box sx={{ p: 3 }} data-testid="cap-tmpl-container">
      <TemplatePageHeader
        templateId={templateId}
        pageLabel="Overview"
        subtitle="Overview of automation performance and configuration."
        testIdPrefix="cap-tmpl-overview"
      />

      <TemplateTabs templateId={templateId} activeValue="overview" />

      {/* KPI row */}
      <Grid
        container
        spacing={2}
        sx={{ mb: 3 }}
        data-testid="cap-tmpl-overview-stats-row"
      >
        <Grid item xs={12} sm={4}>
          <EfficiencyCard templateId={templateId} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <AccuracyCard templateId={templateId} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <CountCard templateId={templateId} />
        </Grid>
      </Grid>

      {/* Manual run — gated on autoconfig perms */}
      <Can
        perform="autoconfig:update"
        yes={() => (
          <Box sx={{ mb: 3 }}>
            <ManualAutoFlowCard template={template} templateId={templateId} />
          </Box>
        )}
      />

      {/* Advanced details (collapsible) — includes child templates, GT dataset, models, raw JSON */}
      <AdvancedDetailsCard
        template={template}
        templateId={templateId}
        groundTruthDataset={groundTruthDataset}
      />
    </Box>
  );
};

AutomationConfigTemplateDetail.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      automation_config_template_id: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
};
